import { baseApi } from '@/api/rtk/baseApi';
import { makeApiCall } from '@/api/makeApiCall';

const authBase = import.meta.env.VITE_AWS_AUTH_API_ENDPOINT;

export const mutationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createRecord: builder.mutation({
      query: (body) => ({
        url: '/create-record/',
        method: 'POST',
        body,
      }),
    }),
    updateReportDetails: builder.mutation({
      query: (dataToSend) => ({
        url: '/update-report/',
        method: 'POST',
        body: dataToSend,
      }),
      invalidatesTags: ['Reports'],
    }),
    updateReportError: builder.mutation({
      query: (body) => ({
        url: '/update-error/',
        method: 'POST',
        body,
      }),
    }),
    getSignedUrl: builder.mutation({
      query: (body) => ({
        url: '/get-signed-url',
        method: 'POST',
        body,
      }),
    }),
    generateTemplate: builder.mutation({
      query: (body) => ({
        url: '/generate-template',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Templates', 'User'],
    }),
    updateTemplate: builder.mutation({
      query: (body) => ({
        url: '/update-template',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Templates', 'User'],
    }),
    createSubstitute: builder.mutation({
      query: (body) => ({
        url: '/create-substitute/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Substitutes'],
    }),
    updateSubstitute: builder.mutation({
      query: (body) => ({
        url: '/update-substitute/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Substitutes'],
    }),
    deleteSubstitute: builder.mutation({
      query: (id) => ({
        url: '/delete-substitute/',
        method: 'POST',
        body: id,
      }),
      invalidatesTags: ['Substitutes'],
    }),
    deleteUser: builder.mutation({
      query: (body) => ({
        url: '/delete-user/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Members'],
    }),
    updateUserRole: builder.mutation({
      query: (body) => ({
        url: '/update-user/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Members'],
    }),
    updateServiceName: builder.mutation({
      query: (body) => ({
        url: '/update-service/',
        method: 'POST',
        body,
      }),
    }),
    setUserLanguage: builder.mutation({
      query: (body) => ({
        url: '/set-language/',
        method: 'POST',
        body,
      }),
      async onQueryStarted({ language }, { dispatch, queryFulfilled }) {
        const userId = localStorage.getItem('user_id');
        if (!userId || !language) return;
        const patch = dispatch(
          baseApi.util.updateQueryData('getUser', { userId }, (draft) => {
            if (draft) draft.language = language;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['User'],
    }),
    getTokenUsage: builder.mutation({
      query: (body) => ({
        url: '/get-token-usage/',
        method: 'POST',
        body,
      }),
    }),
    deleteOrgReports: builder.mutation({
      query: (body) => ({
        url: '/delete-reports/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Reports'],
    }),
    getIntegrationStatus: builder.mutation({
      query: (body) => ({
        url: '/integration-status/',
        method: 'POST',
        body,
      }),
    }),
    createTransaction: builder.mutation({
      query: (body) => ({
        url: '/create-transaction/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Transactions', 'User'],
    }),
    createRecordingLog: builder.mutation({
      query: (body) => ({
        url: '/create-recording-log',
        method: 'POST',
        body,
      }),
    }),
    getReportByIdToken: builder.mutation({
      queryFn: async ({ id, token }) => {
        const result = await makeApiCall({
          path: '/report',
          data: { id },
          customHeaders: { Authorization: `Bearer ${token?.toString()}` },
          requiresAuth: false,
          skipErrorToast: true,
        });
        if (!result?.success) {
          return { error: { status: 400, data: result?.error?.message || 'Failed' } };
        }
        return { data: { response: result.response, responseData: result.data } };
      },
    }),
    createUser: builder.mutation({
      queryFn: async (body) => {
        // Default API (ServerlessRestApi) — same host as get-user; Cognito JWT in header
        const result = await makeApiCall({
          path: '/create-user/',
          data: body,
          requiresAuth: true,
          skipErrorToast: true,
        });
        if (!result?.success) {
          return {
            error: {
              status: 400,
              data: result?.error?.message || result?.data?.error || 'Failed to sign up',
            },
          };
        }
        return { data: result.data };
      },
    }),
    addMember: builder.mutation({
      queryFn: async (body) => {
        const result = await makeApiCall({
          endpoint: authBase,
          path: '/add-new-member/',
          data: body,
          skipErrorToast: true,
        });
        if (!result?.success) {
          return {
            error: {
              status: 400,
              data: result?.error?.message || result?.data?.error || 'Failed to add member',
            },
          };
        }
        return { data: result.data };
      },
      invalidatesTags: ['Members'],
    }),
  }),
});

export const {
  useUpdateReportDetailsMutation,
  useGenerateTemplateMutation,
  useUpdateTemplateMutation,
  useCreateSubstituteMutation,
  useUpdateSubstituteMutation,
  useDeleteSubstituteMutation,
  useAddMemberMutation,
  useDeleteUserMutation,
  useUpdateUserRoleMutation,
  useCreateTransactionMutation,
  useSetUserLanguageMutation,
  useDeleteOrgReportsMutation,
  useGetTokenUsageMutation,
  useGetIntegrationStatusMutation,
} = mutationsApi;
